import {ReactNode, useMemo} from 'react';
import {useSelector} from 'react-redux';

import {OrderDetailsShipmentEvent} from '../../../../types';
import {LabelValue} from '@/types';

import {formatDateUniversal} from '../../../../helpers';

import {selectCarrierLogo} from '@/reducer';

import './index.css';

interface LabelValueRowProps {
  label: ReactNode;
  value: ReactNode;
  containerClass?: string;
  labelClass?: string;
  valueClass?: string;
  colon?: boolean;
}

const LabelValueRow = (props: LabelValueRowProps) => {
  const {
    label,
    value,
    containerClass,
    labelClass,
    valueClass,
    colon = false,
  } = props;

  return (
    <div className={containerClass}>
      <span className={labelClass}>
        {label}
        {colon ? ':' : ''}
      </span>
      <span className={valueClass}>{value}</span>
    </div>
  );
};

interface LabelValueListProps {
  data: LabelValue[];
  className?: string;
  labelClass?: string;
  valueClass?: string;
  colon?: boolean;
  containerClass?: string;
}

const LabelValueList = (props: LabelValueListProps) => {
  const {
    data = [],
    className,
    labelClass,
    valueClass,
    colon = false,
    containerClass,
  } = props;

  return (
    <div className={className}>
      {data.map((item, idx) => (
        <LabelValueRow
          key={`${idx}-${item.label}-${item.value}`}
          label={item.label}
          value={item.value}
          labelClass={labelClass}
          valueClass={valueClass}
          colon={colon}
          containerClass={containerClass}
        />
      ))}
    </div>
  );
};

interface ShipmentTrackingProps {
  trackingEvents?: OrderDetailsShipmentEvent[];
  shipmentUrl?: string;
  shipmentData?: LabelValue[];
  carrier?: string;
}

const ShipmentTracking = (props: ShipmentTrackingProps) => {
  const {trackingEvents = [], shipmentUrl, shipmentData, carrier} = props;

  const logo = useSelector(selectCarrierLogo(carrier));

  const labelValueData = useMemo(
    () =>
      trackingEvents.map(event => ({
        label: formatDateUniversal(event.timestamp),
        value: event.status_message,
      })),
    [trackingEvents]
  );

  const isTrackingContentEmpty = labelValueData.length === 0;

  return (
    <div className="ShipmentTracking">
      <div className="ShipmentTracking__header">
        <div className="ShipmentTracking__title">
          {logo && (
            <img
              src={logo}
              alt={`${carrier} Logo`}
              className="ShipmentTracking__logo"
            />
          )}
          <span className="ShipmentTracking__progress">Shipment Progress</span>
          {shipmentUrl && (
            <span>
              (
              <a href={shipmentUrl} target="_blank" rel="noopener noreferrer">
                Carrier Page Link
              </a>
              )
            </span>
          )}
        </div>
        <LabelValueList
          data={shipmentData}
          className="ShipmentTracking__info-wrapper"
          containerClass="ShipmentTracking__info"
          labelClass="ShipmentTracking__info-label"
          valueClass="ShipmentTracking__info-value"
          colon
        />
      </div>
      {isTrackingContentEmpty ? (
        <div className="ShipmentTracking__content-empty">
          <span className="ShipmentTracking__empty">
            No Tracking Information Available
          </span>
        </div>
      ) : (
        <LabelValueList
          data={labelValueData}
          className="ShipmentTracking__content"
          containerClass="ShipmentTracking__content-item"
          labelClass="ShipmentTracking__content-label"
          valueClass="ShipmentTracking__content-value"
          colon
        />
      )}
    </div>
  );
};

export default ShipmentTracking;
